import { Section } from './Section';
import * as THREE from 'three';
import Time from '../Core/Time';
import { Renderer } from '../Core/Renderer';
import { Camera } from '../Core/Camera';
import { Sizes } from '../Core/Sizes';
import Resources from '../Utils/Resources';
import { SmoothScroll } from '../Core/SmoothScroll';
import vertexShader from '../shaders/gt-umbrella-1/vertex.glsl';
import fragmentShader from '../shaders/gt-umbrella-1/fragment.glsl';
import gradientVertexShader from '../shaders/gradient/vertex.glsl';
import gradientFragmentShader from '../shaders/gradient/fragment.glsl';
import { Input } from '../Core/Input';

export class SectionTrailers extends Section {
  initialize() {
    this.time = Time.getInstance();
    this.renderer = Renderer.getInstance();
    this.resources = Resources.getInstance();
    this.camera = Camera.getInstance();
    this.smoothScroll = SmoothScroll.getInstance();
    this.sizes = Sizes.getInstance();
    this.input = Input.getInstance();

    this.gradient = {
      colorTop: new THREE.Color(0x1373e5),
      colorBottom: new THREE.Color(0x115ebc),
    };

    this.progress = 0;
    this.tiltingProgress = 0;
    this.tiltingProgressLerp = 0.1;
    this.tiltingEdgeBegin = 0.6;
    this.tiltingEdgeEnd = 0.8;

    this.shiftFromTopBegin = 0;
    this.shiftFromTop = this.shiftFromTopBegin;
    this.shiftFromTopEnd = 0.3;
    this.shiftFromTopLerp = 0.1;
    this.shiftFromTopEdgeBegin = 0.5;
    this.shiftFromTopEdgeEnd = 0.8;
    this.shiftFromTopProgress = 0;

    this.umbrellaPositionZ = 0.001;
    this.umbrellaPositionX = {
      current: 0,
      target: 0,
      lerp: 0.1,
    };

    this.bindAll();
    this.createGradientBackground();
    this.createUmbrellaPlane();
    this.enableGrainEffect();
    this.initEvents();

    // Set up resource loading listener
    this.resources.on("resource", (resourceName) => {
      if(resourceName === 'umbrella_01'){
        this.initUmbrellaTexture();
        this.setupHTML();
      }
    });
  }

  setupHTML(){
    this.html = document.getElementById('episodes');
    this.html.classList.add('is-loaded');
  }

  bindAll() {
    this.onProgress = this.onProgress.bind(this);
    this.onTick = this.onTick.bind(this);
    this.onInputUpdate = this.onInputUpdate.bind(this);
  }

  /**
   * Create background with vertical gradient
   */
  createGradientBackground() {
    // Create a simple 1x1 plane geometry
    const planeGeometry = new THREE.PlaneGeometry(1, 1);

    // Create shader material
    this.backgroundMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uColorTop: { value: this.gradient.colorBottom },
        uColorBottom: { value: this.gradient.colorTop },
      },
      vertexShader: gradientVertexShader,
      fragmentShader: gradientFragmentShader,
      transparent: true,
      side: THREE.DoubleSide,
    });

    // Create mesh
    this.backgroundPlane = new THREE.Mesh(
      planeGeometry,
      this.backgroundMaterial
    );
    this.backgroundPlane.name = `${this.constructor.name}-BackgroundPlane`;

    // Scale the mesh to match section dimensions
    this.setBackgroundSize();

    // Add to group
    this.group.add(this.backgroundPlane);
  }

  /**
   * Create a plane for umbrella without texture
   */
  createUmbrellaPlane() {
    const geometry = new THREE.PlaneGeometry(1, 1);

    this.planeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uDiffuseTexture: { value: null }, // Will be updated when texture is ready
        uProgress: { value: this.progress },
        uShiftFromTop: { value: 0.1 },
        uUmbrellaPositionX: { value: 0 },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
    });

    this.umbrella = new THREE.Mesh(geometry, this.planeMaterial);
    this.umbrella.name = `${this.constructor.name}-Umbrella`;

    this.setUmbrellaSize();
    this.group.add(this.umbrella);
  }

  /**
   * Initialize umbrella texture when resource is ready
   */
  initUmbrellaTexture() {
    if (!this.resources.items.umbrella_01) return;

    this.umbrella1Texture = this.resources.items.umbrella_01;
    this.planeMaterial.uniforms.uDiffuseTexture.value = this.umbrella1Texture;
    
    // Update size after texture is loaded to ensure correct aspect ratio
    this.setUmbrellaSize();
  }

  setBackgroundSize() {
    this.backgroundPlane.scale.set(
      this.sectionData.worldData.width,
      this.sectionData.worldData.height,
      1
    );
  }

  initEvents() {
    this.camera.on("scroll", this.onProgress);
    this.time.on("tick", this.onTick);
    this.input.on("update", this.onInputUpdate);
  }

  onInputUpdate() {
    if(!this.visible) return;

    const normalized = (this.input.mouseNormalized.x + 1) / 2;

    let eased;
    if (normalized < 0.5) {
      eased = 2 * normalized * normalized;
    } else {
      eased = 1 - Math.pow(-2 * normalized + 2, 2) / 2;
    }

    this.umbrellaPositionX.target = eased * 0.4 - 1;
  }

  onProgress() {
    if(!this.visible) return;

    this.tiltingProgress =
      (this.progress - this.tiltingEdgeBegin) /
      (this.tiltingEdgeEnd - this.tiltingEdgeBegin);
    this.tiltingProgress = Math.max(0, Math.min(1, this.tiltingProgress));

    let linearShiftProgress =
      (this.progress - this.shiftFromTopEdgeBegin) /
      (this.shiftFromTopEdgeEnd - this.shiftFromTopEdgeBegin);
    linearShiftProgress = Math.max(0, Math.min(1, linearShiftProgress));

    this.shiftFromTopProgress =
      linearShiftProgress * linearShiftProgress * (3 - 2 * linearShiftProgress);

    this.shiftFromTop =
      this.shiftFromTopBegin +
      (this.shiftFromTopEnd - this.shiftFromTopBegin) *
        this.shiftFromTopProgress;

    if (this.planeMaterial) {
      this.planeMaterial.uniforms.uProgress.value = 1 - this.tiltingProgress;
      this.planeMaterial.uniforms.uShiftFromTop.value = this.shiftFromTop;
    }
  }

  onTick() {
    if(!this.visible) return;

    this.umbrellaPositionX.current = this.umbrellaPositionX.current + (this.umbrellaPositionX.target - this.umbrellaPositionX.current) * this.umbrellaPositionX.lerp;
    if(this.planeMaterial){
      this.planeMaterial.uniforms.uUmbrellaPositionX.value = this.umbrellaPositionX.current;
    }

    if(this.umbrella){
      this.umbrella.position.x = this.umbrellaPositionX.initial - this.umbrellaPositionX.current * 0.5;
    }
  }

  setUmbrellaSize() {
    const sectionWidth = this.sectionData.worldData.width;
    const sectionHeight = this.sectionData.worldData.height;

    // Calculate size (70% of screen size but not bigger than 1024px)
    const screenWidthPixels = this.sizes.width * 0.7;
    const screenHeightPixels = this.sizes.height * 0.7;

    // Determine aspect ratio of the texture to maintain proportions
    const textureAspect = this.umbrella1Texture?.image
      ? this.umbrella1Texture.image.width / this.umbrella1Texture.image.height
      : 1.0;

    // Calculate dimensions in pixels
    let planeWidthPixels, planeHeightPixels;

    if (textureAspect >= 1) {
      // Texture is wider than tall
      planeWidthPixels = Math.min(screenWidthPixels, 1024);
      planeHeightPixels = planeWidthPixels / textureAspect;
    } else {
      // Texture is taller than wide
      planeHeightPixels = Math.min(screenHeightPixels, 1024);
      planeWidthPixels = planeHeightPixels * textureAspect;
    }

    // Convert to world units
    const planeWidth = this.sizes.pixelsToWorldUnits(planeWidthPixels);
    const planeHeight = this.sizes.pixelsToWorldUnits(planeHeightPixels);

    // Scale the plane
    this.umbrella.scale.set(planeWidth, planeHeight, 1);

    // Calculate margins (10% of section dimensions)
    const marginX = -sectionWidth * 0.1;
    const marginY = -sectionHeight * 0;

    // Position in bottom left corner with margin
    this.umbrellaPositionX.initial = -(sectionWidth / 2) + planeWidth / 2 + marginX;
    this.umbrella.position.x = this.umbrellaPositionX.initial;
    this.umbrella.position.y = -(sectionHeight / 2) + planeHeight / 2 + marginY;
    this.umbrella.position.z = this.umbrellaPositionZ; // Slightly in front of background
  }

  /**
   * Enable grain effect for all objects in this section
   */
  enableGrainEffect() {
    // Enable grain effect on the group itself
    this.renderer.enableGrainForObject(this.group);

    // Traverse the group and enable grain for all objects
    this.group.traverse((object) => {
      this.renderer.enableGrainForObject(object);
    });

    this.renderer.disableGrainForObject(this.umbrella);

    // Configure grain effect parameters
    this.renderer.configureGrainEffect({
      uRadialEllipse: this.resources.items.radialEllipse,
      uRadialEllipseSize: 0.5,
    });
  }

  resize() {
    this.setUmbrellaSize();
    this.setBackgroundSize();
  }

  /**
   * Dispose section elements
   * Override base method
   */
  disposeElements() {
    this.time.off("tick");

    if (this.umbrella) {
      this.umbrella.geometry.dispose();
      this.planeMaterial.dispose();
      this.umbrella = null;
      this.planeMaterial = null;
    }

    if (this.backgroundPlane) {
      this.backgroundPlane.geometry.dispose();
      this.backgroundMaterial.dispose();
      this.backgroundPlane = null;
      this.backgroundMaterial = null;
    }
  }
} 